import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useGetFeedMatchesQuery } from '@/api/feed/getFeedMatches';
import { useGetFeedTeamsQuery } from '@/api/feed/getFeedTeams';
import {
  Settings,
  MoreVertical,
  BarChart3,
  List,
  AlertCircle,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { TzDateDisplay } from '@/components/datetime/TzDateDisplay';
import { TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Tooltip } from '@/components/ui/tooltip';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { MatchStatsFeedSheet } from '@/components/live-scoring/MatchStatsFeedSheet';
import { FeedStatusBadge } from '@/components/live-scoring/FeedStatusBadge';
import { useGetFeedStatusesQuery } from '@/api/live-scoring/getFeedStatuses';
import { isIndividualSport } from '@low6-pickem/sports-config';

export const Route = createFileRoute('/dashboard/sports/$id/')({
  component: RouteComponent,
});

interface MatchData {
  id: string;
  scheduleId?: string;
  homeAbbr: string;
  awayAbbr: string;
  scoreText: string;
  status: string;
  date: string;
  homeTeamName: string;
  awayTeamName: string;
  homeScore?: number;
  awayScore?: number;
  scheduledDate: string;
}

function RouteComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [dateRangeError, setDateRangeError] = useState<string>('');
  const [feedsMatch, setFeedsMatch] = useState<MatchData | null>(null);
  const [expandedTournaments, setExpandedTournaments] = useState<Set<string>>(new Set());

  // Convert route id to lowercase for API (NFL -> nfl, NBA -> nba)
  const leagueId = id.toLowerCase();
  const isIndividual = isIndividualSport(id);

  const toggleTournament = (tournamentId: string) => {
    setExpandedTournaments(prev => {
      const next = new Set(prev);
      if (next.has(tournamentId)) {
        next.delete(tournamentId);
      } else {
        next.add(tournamentId);
      }
      return next;
    });
  };

  // Use startDate if provided, otherwise default to today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Validate date range (max 31 days)
  const validateDateRange = (start: string, end: string): string | null => {
    if (!start || !end) return null;

    const startDateObj = new Date(start);
    const endDateObj = new Date(end);

    // Check if end date is before start date
    if (endDateObj < startDateObj) {
      return 'End date must be after start date';
    }

    // Calculate difference in days
    const daysDiff = Math.ceil(
      (endDateObj.getTime() - startDateObj.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysDiff > 31) {
      return 'Date range too large. Maximum allowed range is 31 days (approximately 1 month). Please select a smaller date range.';
    }

    return null;
  };

  // Validate whenever dates change
  const dateValidationError = useMemo(() => {
    if (startDate && endDate) {
      const error = validateDateRange(startDate, endDate);
      setDateRangeError(error || '');
      return error;
    }
    setDateRangeError('');
    return null;
  }, [startDate, endDate]);

  // Determine query parameters: use date range if both dates are provided, otherwise use single date
  const queryDate = startDate && endDate ? undefined : startDate || getTodayDate();
  const queryFrom = startDate && endDate ? startDate : undefined;
  const queryTo = startDate && endDate ? endDate : undefined;

  // Only query if date range is valid
  const shouldQuery = !dateValidationError;

  const {
    data: matches = [],
    isLoading: matchesLoading,
    error: matchesError,
  } = useGetFeedMatchesQuery(
    leagueId,
    isIndividual ? undefined : queryDate,
    isIndividual ? undefined : queryFrom,
    isIndividual ? undefined : queryTo,
    { enabled: isIndividual ? true : shouldQuery }
  );
  const { data: teams = [] } = useGetFeedTeamsQuery(leagueId);

  // Create a map of team IDs to team data
  const teamMap = useMemo(() => {
    const map = new Map();
    teams.forEach(team => {
      map.set(team.externalId, { name: team.name, abbreviation: team.abbreviation || 'UNK' });
    });
    return map;
  }, [teams]);

  // For individual sports: group matches (rounds) by tournament ID
  const tournamentRoundsMap = useMemo(() => {
    if (!isIndividual) return new Map<string, typeof matches>();
    const map = new Map<string, typeof matches>();
    for (const match of matches) {
      // Match ID format: "tournamentId:round_N"
      const tournamentId = match.externalId.split(':')[0];
      if (!map.has(tournamentId)) map.set(tournamentId, []);
      map.get(tournamentId)!.push(match);
    }
    return map;
  }, [matches, isIndividual]);

  const formattedMatches = useMemo(() => {
    return matches.map(match => {
      const homeTeam = teamMap.get(match.homeTeamId) || { name: 'Home Team', abbreviation: 'HOME' };
      const awayTeam = teamMap.get(match.awayTeamId) || { name: 'Away Team', abbreviation: 'AWAY' };

      const hasScores = typeof match.homeScore === 'number' && typeof match.awayScore === 'number';
      const scoreText = hasScores ? `${match.homeScore} : ${match.awayScore}` : '- : -';

      // Generate schedule ID similar to the image format
      const matchDate = new Date(match.scheduledDate);
      const dateStr = matchDate.toISOString().split('T')[0].replace(/-/g, '_');
      const scheduleId = `${leagueId.toUpperCase()}_${dateStr}_${awayTeam.abbreviation}@${homeTeam.abbreviation}_RADAR${match.externalId.slice(-4)}`;

      return {
        id: match.externalId,
        scheduleId,
        homeAbbr: homeTeam.abbreviation,
        awayAbbr: awayTeam.abbreviation,
        scoreText,
        status: match.status,
        date: match.scheduledDate,
        homeTeamName: homeTeam.name,
        awayTeamName: awayTeam.name,
        homeScore: match.homeScore,
        awayScore: match.awayScore,
        scheduledDate: match.scheduledDate,
      } as MatchData;
    });
  }, [matches, teamMap, leagueId]);

  const isLoading = matchesLoading;

  // Batch fetch feed-status summaries for all non-live fixtures in one call.
  // Live fixtures pull their status from the match-stats SSE stream instead.
  const nonLiveFixtureIds = useMemo(
    () => formattedMatches.filter(m => m.status !== 'live').map(m => m.id),
    [formattedMatches]
  );
  const {
    data: feedStatusMap,
    isLoading: feedStatusesLoading,
    isError: feedStatusesError,
  } = useGetFeedStatusesQuery(nonLiveFixtureIds);

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      scheduled: {
        label: 'Upcoming',
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      },
      live: {
        label: 'In Play',
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      },
      finished: {
        label: 'Finished',
        className: 'bg-gray-200 text-gray-900 dark:bg-gray-600 dark:text-gray-100',
      },
      postponed: {
        label: 'Postponed',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      },
      cancelled: {
        label: 'Cancelled',
        className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      },
    };

    const statusInfo = statusMap[status] || {
      label: status,
      className: 'bg-gray-100 text-gray-800',
    };

    return (
      <Badge className={statusInfo.className} variant="outline">
        {statusInfo.label}
      </Badge>
    );
  };

  const sportName = id.toUpperCase();

  return (
    <PageContainer>
      <Breadcrumbs
        overrides={{ sports: 'Sports' }}
        pathOverrides={{ sports: `/dashboard/sports/${id}` }}
      />

      <div className="flex gap-4 items-center mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-gray-500" />
          <h1 className="text-2xl font-semibold">{sportName}</h1>
        </div>

        {!isIndividual && (
          <>
            <Input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="w-[180px]"
              placeholder="Start Date"
            />

            <Input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="w-[180px]"
              placeholder="End Date"
            />
          </>
        )}

        {isIndividual && (
          <span className="text-sm text-muted-foreground">{teams.length} tournaments</span>
        )}

        <div className="flex gap-2 ml-auto">
          {!isIndividual && (
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/dashboard/sports/$id/teams', params: { id } })}
            >
              Teams
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate({ to: '/dashboard/sports/$id/players', params: { id } })}
          >
            Players
          </Button>
          {/* <Button
            variant={activeView === 'feeds' ? 'default' : 'outline'}
            onClick={() => setActiveView('feeds')}
            className={activeView === 'feeds' ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
          >
            <List className="mr-2 h-4 w-4" />
            Incoming Feeds
          </Button>
          <Button variant="outline">
            <Plus className="mr-2 h-4 w-4" />
            Add Match
          </Button> */}
        </div>
      </div>

      {dateRangeError && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Invalid Date Range</AlertTitle>
          <AlertDescription>{dateRangeError}</AlertDescription>
        </Alert>
      )}

      {matchesError && !dateRangeError && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Fixtures</AlertTitle>
          <AlertDescription>
            {matchesError instanceof Error
              ? matchesError.message
              : 'Failed to load fixtures. Please try again with a smaller date range (maximum 31 days).'}
          </AlertDescription>
        </Alert>
      )}

      <ContentCard>
        {isIndividual ? (
          /* Tournaments table with expandable rounds for individual sports (PGA) */
          <Table>
            <TableHeader className="[&_tr]:border-b-0">
              <TableRow>
                <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-l-xl font-bold w-8"></TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Tournament</TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Venue</TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Dates</TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Purse</TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-r-xl font-bold">
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teams.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <List className="h-12 w-12 text-gray-400" />
                      <p className="text-gray-600 dark:text-gray-400">No tournaments found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                teams.map(tournament => {
                  const isExpanded = expandedTournaments.has(tournament.externalId);
                  const rounds = tournamentRoundsMap.get(tournament.externalId) || [];
                  return (
                    <>
                      <TableRow
                        key={tournament.externalId}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900"
                        onClick={() => toggleTournament(tournament.externalId)}
                      >
                        <TableCell className="w-8 pr-0">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-500" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{tournament.name}</TableCell>
                        <TableCell>{tournament.venue || tournament.market || '-'}</TableCell>
                        <TableCell className="text-sm">
                          {tournament.startDate && tournament.endDate
                            ? `${new Date(tournament.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(tournament.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                            : '-'}
                        </TableCell>
                        <TableCell className="font-mono">
                          {tournament.purse ? `$${tournament.purse.toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell>
                          {tournament.status &&
                            getStatusBadge(
                              tournament.status === 'closed' ? 'finished' : tournament.status
                            )}
                        </TableCell>
                      </TableRow>
                      {isExpanded &&
                        rounds.length > 0 &&
                        rounds.map(round => {
                          const roundNum = round.externalId.split(':round_')[1];
                          return (
                            <TableRow
                              key={round.externalId}
                              className="bg-gray-50/50 dark:bg-gray-900/50"
                            >
                              <TableCell></TableCell>
                              <TableCell className="pl-8">
                                <span className="text-sm font-medium">Round {roundNum}</span>
                              </TableCell>
                              <TableCell></TableCell>
                              <TableCell className="text-sm">
                                <TzDateDisplay
                                  utcDate={round.scheduledDate}
                                  formatString="EEE, MMM d yyyy"
                                  showTimezoneTooltip={false}
                                  showUtcBadge={false}
                                  dateOnly={true}
                                />
                              </TableCell>
                              <TableCell>
                                <span className="text-xs text-muted-foreground font-mono">
                                  {round.externalId}
                                </span>
                              </TableCell>
                              <TableCell>{getStatusBadge(round.status)}</TableCell>
                            </TableRow>
                          );
                        })}
                      {isExpanded && rounds.length === 0 && (
                        <TableRow className="bg-gray-50/50 dark:bg-gray-900/50">
                          <TableCell></TableCell>
                          <TableCell colSpan={5} className="text-sm text-muted-foreground py-3">
                            {matchesLoading
                              ? 'Loading rounds...'
                              : 'No rounds found for this tournament'}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })
              )}
            </TableBody>
          </Table>
        ) : (
          <Table>
            <TableHeader className="[&_tr]:border-b-0">
              <TableRow>
                <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-l-xl font-bold">
                  ID
                </TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                  Schedule ID
                </TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold w-[180px]">
                  Score
                </TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Status</TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Feed</TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Date</TableHead>
                <TableHead className="text-right bg-gray-100 dark:bg-gray-800 rounded-r-xl font-bold">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-64" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-40" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-8 ml-auto" />
                    </TableCell>
                  </TableRow>
                ))
              ) : formattedMatches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <List className="h-12 w-12 text-gray-400" />
                      <p className="text-gray-600 dark:text-gray-400">No matches found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                formattedMatches.map((match: MatchData) => (
                  <TableRow key={match.id}>
                    <TableCell className="font-medium">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="block max-w-[60px] truncate">{match.id}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{match.id}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="font-medium max-w-[300px] truncate">
                      {match.scheduleId}
                    </TableCell>
                    <TableCell className="w-[180px]">
                      <div className="grid grid-cols-3 items-center gap-2 w-[150px] tabular-nums whitespace-nowrap">
                        <span className="text-left font-medium">{match.homeAbbr}</span>
                        <span className="text-center text-muted-foreground">{match.scoreText}</span>
                        <span className="text-right font-medium">{match.awayAbbr}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(match.status)}</TableCell>
                    <TableCell>
                      <FeedStatusBadge
                        fixtureId={match.id}
                        matchStatus={match.status}
                        snapshot={feedStatusMap?.get(match.id)}
                        snapshotLoading={feedStatusesLoading}
                        snapshotError={feedStatusesError}
                      />
                    </TableCell>
                    <TableCell>
                      <TzDateDisplay
                        utcDate={match.date}
                        formatString="d MMM yyyy h:mm a"
                        showTimezoneTooltip={false}
                        showUtcBadge={false}
                        dateOnly={false}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link
                              to="/dashboard/sports/$id/matches/$matchId/stats"
                              params={{ id, matchId: match.id }}
                              className="flex items-center cursor-pointer"
                            >
                              <BarChart3 className="mr-2 h-4 w-4" />
                              View Stats
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setFeedsMatch(match)}>
                            <List className="mr-2 h-4 w-4" />
                            View Feeds
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </ContentCard>
      <MatchStatsFeedSheet
        match={feedsMatch}
        open={!!feedsMatch}
        onOpenChange={open => {
          if (!open) setFeedsMatch(null);
        }}
      />
    </PageContainer>
  );
}
